import { routePatterns } from "@zplanem/web-paths"
import { Route, BrowserRouter as Router, Routes } from "react-router-dom"
import { ActiveChatProvider } from "./active-chat-context"
import { useAuth } from "./auth-context"
import { AuthenticatedShell } from "./components/authenticated-shell"
import { GlobalHeader } from "./components/global-header"
import { LoadingScreen } from "./components/loading-screen"
import { ProtectedRoute } from "./components/protected-route"
import { useDocumentAccessRealtime } from "./hooks/use-document-access-realtime"
import { useMentionToasts } from "./hooks/use-mention-toasts"
import { useNotificationPreferenceInvalidation } from "./hooks/use-notification-preference-invalidation"
import { useProjectAccessRealtime } from "./hooks/use-project-access-realtime"
import { useTicketRealtimeInvalidation } from "./hooks/use-ticket-realtime-invalidation"
import { getTranslations } from "./i18n"
import { useLocale } from "./locale-context"
import { AuthPage } from "./pages/auth"
import { DashboardPage } from "./pages/dashboard"
import { InvitationAcceptPage } from "./pages/invitation-accept"
import { LandingPage } from "./pages/landing"
import { MyTicketsPage } from "./pages/my-tickets"
import { NotFoundPage } from "./pages/not-found"
import { OnboardingPage } from "./pages/onboarding"
import { ProfilePage } from "./pages/profile"
import { ProjectBudgetTab } from "./pages/project/project-budget-tab"
import { ProjectDetailsTab } from "./pages/project/project-details-tab"
import { ProjectDocumentsTab } from "./pages/project/project-documents-tab"
import { ProjectInspirationsTab } from "./pages/project/project-inspirations-tab"
import { ProjectMembersTab } from "./pages/project/project-members-tab"
import { ProjectTasksTab } from "./pages/project/project-tasks-tab"
import { ProjectVisualizationsTab } from "./pages/project/project-visualizations-tab"
import { ProjectDetailLayout } from "./pages/project-detail"
import { ProjectsListPage } from "./pages/projects-list"
import { PropertyBudgetPage } from "./pages/property-budget"
import { PropertyDetailPage } from "./pages/property-detail"
import { PropertyDocumentsPage } from "./pages/property-documents"
import { PropertyEditPage } from "./pages/property-edit"
import { PropertyJournalPage } from "./pages/property-journal"
import { PropertyNewPage } from "./pages/property-new"
import { PropertyTicketsPage } from "./pages/property-tickets"
import { PropertyTransferPage } from "./pages/property-transfer"
import { SettingsPage } from "./pages/settings"
import { TicketDetailPage } from "./pages/ticket-detail"
import { ToastProvider } from "./toast-context"
import { useUser } from "./user-context"

/** Side-effect-only: subscribes to the user's app-wide mention channel. */
function MentionToastListener({ userId }: { userId: string | undefined }) {
	useMentionToasts(userId)
	return null
}

function TicketRealtimeInvalidationListener({
	userId,
}: {
	userId: string | undefined
}) {
	useTicketRealtimeInvalidation(userId)
	return null
}

function NotificationPreferenceInvalidationListener({
	userId,
}: {
	userId: string | undefined
}) {
	useNotificationPreferenceInvalidation(userId)
	return null
}

function DocumentAccessRealtimeListener({
	userId,
}: {
	userId: string | undefined
}) {
	useDocumentAccessRealtime(userId)
	useProjectAccessRealtime(userId)
	return null
}

function App() {
	const { user, loading } = useAuth()
	const { profile } = useUser()
	const { locale } = useLocale()
	const t = getTranslations(locale)

	// Hold the loading screen while Firebase is still resolving OR while
	// we have an authenticated user but UserProvider hasn't returned /me
	// yet. Without the second guard, the protected tree would mount
	// briefly with profile=null on first sign-up — flashing /dashboard
	// and firing GET /properties before the onboarding gate kicks in.
	if (loading || (user && profile === null)) {
		return <LoadingScreen label={t.common.loading} />
	}

	const onboardingPending = Boolean(
		user && profile && !profile.isOnboardingComplete,
	)

	return (
		<Router>
			<ActiveChatProvider>
				<ToastProvider>
					<MentionToastListener userId={profile?.id} />
					<TicketRealtimeInvalidationListener userId={profile?.id} />
					<DocumentAccessRealtimeListener userId={profile?.id} />
					<NotificationPreferenceInvalidationListener userId={profile?.id} />
					<GlobalHeader />
					<Routes>
						<Route path={routePatterns.landing} element={<LandingPage />} />
						<Route path={routePatterns.auth} element={<AuthPage />} />
						{/*
						 * Public invitation-accept route. Declared BEFORE the
						 * onboarding-pending wildcard so an invitee who hasn't finished
						 * onboarding (or hasn't signed in yet) can still land here. The
						 * underlying accept-invitation API uses `authenticateUser`, not
						 * `authenticateOnboardedUser`, matching this access semantics.
						 */}
						<Route
							path={routePatterns.invitation}
							element={<InvitationAcceptPage />}
						/>
						{onboardingPending ? (
							<Route path="*" element={<OnboardingPage />} />
						) : (
							<>
								<Route
									path={routePatterns.dashboard}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<DashboardPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.myTickets}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<MyTicketsPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.newProperty}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyNewPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.property}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyDetailPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.propertyProjects}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<ProjectsListPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.project}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<ProjectDetailLayout />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								>
									<Route index element={<ProjectDetailsTab />} />
									<Route path="tasks" element={<ProjectTasksTab />} />
									<Route
										path="visualizations"
										element={<ProjectVisualizationsTab />}
									/>
									<Route
										path="inspirations"
										element={<ProjectInspirationsTab />}
									/>
									<Route path="documents" element={<ProjectDocumentsTab />} />
									<Route path="budget" element={<ProjectBudgetTab />} />
									<Route path="members" element={<ProjectMembersTab />} />
									{/* ZPL-120: project journal has no dedicated page — it is a
							    section on the details tab. Make the canonical
							    /properties/:id/projects/:projectId/journal URL routable
							    (deep-linkable + Mareczek-link target) by rendering the
							    details tab; the #journal anchor scrolls to the section. */}
									<Route path="journal" element={<ProjectDetailsTab />} />
								</Route>
								<Route
									path={routePatterns.propertyJournal}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyJournalPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.propertyDocuments}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyDocumentsPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.propertyBudget}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyBudgetPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.propertyTickets}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyTicketsPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.ticket}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<TicketDetailPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.propertyEdit}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyEditPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.propertyTransfer}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<PropertyTransferPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.profile}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<ProfilePage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								<Route
									path={routePatterns.settings}
									element={
										<ProtectedRoute>
											<AuthenticatedShell>
												<SettingsPage />
											</AuthenticatedShell>
										</ProtectedRoute>
									}
								/>
								{/* ZPL-120: authenticated catch-all. Without it an own-origin
						    URL with no matching route (incl. a COLD direct-open or an
						    own-origin link Mareczek emits for a screen this client
						    version lacks) renders blank. NotFoundPage wraps the shell. */}
								<Route
									path="*"
									element={
										<ProtectedRoute>
											<NotFoundPage />
										</ProtectedRoute>
									}
								/>
							</>
						)}
					</Routes>
				</ToastProvider>
			</ActiveChatProvider>
		</Router>
	)
}

export default App
